import { Component, OnInit, signal } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import _ from 'lodash';
import { ITableColumn } from 'src/app/models/tableColumns';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ITablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { aiPreventionMockTableColumns } from 'src/app/models/tableColumns/aiPreventionMockTableColumn.model';
import { AiPreventionMockTableRowViewModel } from 'src/app/models/viewModels/aiPreventionMockTableRowViewModel';
import { AiPreventionMockHttpService } from 'src/app/services/httpServices/detection/aiPreventionMockHttpService/ai-prevention-mock-http.service';
import { PreventionMockModel } from 'src/app/models/api/preventionMock.model';
import { formatFromDate } from 'src/app/common/datetime_utility';
import moment from 'moment';
import { getAiServiceLogoPath } from 'src/app/common/aiServiceUtils';
import { SidePanelService } from '../../../features/side-panel/services/side-panel.service';

@Component({
  selector: 'app-enterprise-ai-prevention-mock',
  templateUrl: './enterprise-ai-prevention-mock.component.html',
  styleUrls: ['./enterprise-ai-prevention-mock.component.scss'],
})
export class EnterpriseAiPreventionMockComponent implements OnInit {
  public columns = signal<ITableColumn[]>(aiPreventionMockTableColumns);
  public preventionCurrentPage$ = new BehaviorSubject<AiPreventionMockTableRowViewModel[]>([]);
  public isLoading = signal<boolean>(true);
  public showNoData = false;
  public totalItems = 0;
  public pageInfo$: BehaviorSubject<ITablePageInfo>;
  public preventionData: PreventionMockModel[] = [];
  public lastUpdated: Date;
  public sortedColumn$ = new BehaviorSubject<SortColumn | undefined>(undefined);

  public tableColumns = aiPreventionMockTableColumns;
  public currentRulesViewModel: BehaviorSubject<BehaviorSubject<AiPreventionMockTableRowViewModel>[]>;
  private _sortOnChangeSubscription$: Subscription;

  constructor(
    private readonly _aiPreventionHttpService: AiPreventionMockHttpService,
    private readonly _sidePanel: SidePanelService
  ) {
    this._initializePageInfo();
  }

  ngOnInit(): void {
    this.fetchPreventionData();
    this._subscribeToSortOnChange();
    this.lastUpdated = moment(new Date()).add(-5, 'minutes').toDate();
  }

  public fetchPreventionData(): void {
    this.isLoading.set(true);
    this._aiPreventionHttpService.getPreventionMockData().subscribe(
      (data: PreventionMockModel[]) => {
        this.preventionData = data;
        this.totalItems = this.preventionData.length;
        console.log('Prevention data:', this.preventionData);

        const viewModels = this.preventionData.map((item) => this._transformToViewModel(item));
        this.preventionCurrentPage$.next(viewModels);

        this.showNoData = viewModels.length === 0;
        this.isLoading.set(false);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error: any) => {
        console.error('Error fetching prevention data:', error);
        this.showNoData = true;
        this.isLoading.set(false);
        this.preventionCurrentPage$.next([]);
      }
    );
  }

  public rowSelected(selectedRow: AiPreventionMockTableRowViewModel): void {
    this._sidePanel.openPreventionDetails(selectedRow);
  }

  public rowUnselected = () => this._sidePanel.close();

  public onColumnSortClick(sortData: SortColumn): void {
    this.sortedColumn$.next(sortData);
  }

  private _transformToViewModel(row: PreventionMockModel): AiPreventionMockTableRowViewModel {
    return {
      rowId: row.id,
      id: row.id,
      name: row.name,
      integrationId: row.integrationId,
      type: row.type,
      status: row.status,
      size: row.size,
      dateCreatedSent: formatFromDate(row.dateCreatedSent),
      ownerCreatedSentBy: row.ownerCreatedSentBy,
      prevention: row,
      agent: row.agent,
      clickable: true,
      aiServiceIcon: getAiServiceLogoPath(row.aiService),
      menuActions: [],
    };
  }

  private _initializePageInfo() {
    this.pageInfo$ = new BehaviorSubject({
      number: 1,
      currSize: 1,
      maxSize: 2,
      totalItems: 1,
      totalPages: 1,
    });
  }

  private _subscribeToSortOnChange(): void {
    this._sortOnChangeSubscription$ = this.sortedColumn$.subscribe((newSort) => {
      if (newSort?.column.sortKey == 'issueName') {
        this._sortByNumbers(newSort.isDesc);
      } else if (newSort?.column.sortKey == 'severity') {
        this._sortBySeverity(newSort.isDesc);
      }
    });
  }

  private _sortByNumbers(isDesc: boolean): void {
    const isDescStr = isDesc ? 'desc' : 'asc';

    this.currentRulesViewModel = new BehaviorSubject(
      _.orderBy(this.currentRulesViewModel.value, ['value.issueName'], [isDescStr])
    );
  }

  private _sortBySeverity(isDesc: boolean): void {
    const isDescStr = isDesc ? 'desc' : 'asc';

    this.currentRulesViewModel = new BehaviorSubject(
      _.orderBy(this.currentRulesViewModel.value, ['value.severity'], [isDescStr])
    );
  }
}
